import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { logger } from '../utils/logger';

// Crear el contexto de autenticación
const AuthContext = createContext();

// Proveedor del contexto de autenticación
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  
  // Configurar la URL base para todas las peticiones
  // If BACKEND_URL already contains /api, don't add it again
  const baseURL = process.env.REACT_APP_BACKEND_URL || '';
  axios.defaults.baseURL = baseURL;

  /**
   * Función para cargar los datos del usuario
   * Extrayendo esta función podemos reutilizarla en diferentes partes
   * del flujo de autenticación
   */
  const fetchUserData = async (authToken) => {
    try {
      // Asegurar que el token está configurado en las cabeceras
      const tokenToUse = authToken || token;
      if (!tokenToUse) {
        return { success: false, error: 'No hay token disponible' };
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${tokenToUse}`;
      
      // Obtener la información del usuario actual
      const response = await axios.get('/auth/me');
      return { success: true, data: response.data };
    } catch (error) {
      logger.error('Error al obtener datos de usuario:', error);
      return { success: false, error };
    }
  };

  // Verificar si el usuario está autenticado cuando cambia el token
  useEffect(() => {
    let isMounted = true;
    setAuthError(null);
    
    const verifyAuthentication = async () => {
      if (!token) {
        if (isMounted) {
          setLoading(false);
          setCurrentUser(null);
        }
        return;
      }

      setLoading(true);
      
      // Intentar cargar datos del usuario
      const result = await fetchUserData();
      
      if (isMounted) {
        if (result.success) {
          setCurrentUser(result.data);
          setAuthError(null);
        } else {
          // Limpiar todo en caso de error
          localStorage.removeItem('token');
          setToken(null);
          setCurrentUser(null);
          delete axios.defaults.headers.common['Authorization'];
          setAuthError('Sesión expirada o inválida');
        }
        setLoading(false);
      }
    };

    verifyAuthentication();
    
    // Cleanup function para evitar actualizar estado después de desmontado
    return () => {
      isMounted = false;
    };
  }, [token]);

  // Iniciar sesión
  const login = async (email, password) => {
    try {
      // Convertir a formato FormData para compatibilidad con FastAPI OAuth2
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { access_token } = response.data;
      
      // Guardar el token en localStorage y en el estado
      localStorage.setItem('token', access_token);
      
      // Inmediatamente intentamos cargar los datos del usuario después de un login exitoso
      const userResult = await fetchUserData(access_token);
      
      if (userResult.success) {
        // Actualizamos el estado con los datos del usuario
        setCurrentUser(userResult.data);
        logger.log('Usuario autenticado correctamente:', userResult.data.full_name);
      }
      
      // Actualizamos el token después de cargar los datos para
      // evitar que se dispare el useEffect antes de tener datos
      setToken(access_token);
      
      return { 
        success: true, 
        userData: userResult.success ? userResult.data : null 
      };
    } catch (error) {
      logger.error('Error de inicio de sesión:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Error de inicio de sesión'
      };
    }
  };

  // Registrar usuario
  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error) {
      logger.error('Error de registro:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Error de registro'
      };
    }
  };

  // Cerrar sesión
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Verificar si el token ha expirado
  const isTokenExpired = () => {
    if (!token) return true;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch (error) {
      return true;
    }
  };

  // Verificar si el usuario está autenticado
  const isTokenValid = !isTokenExpired();
  
  // Calcular si el usuario está autenticado usando todos los factores
  const isAuthenticated = !!token && !!currentUser && isTokenValid;

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isAuthenticated,
        login,
        logout,
        register,
        loading,
        isTokenExpired,
        authError,
        fetchUserData, // Exponemos la función para que otros componentes puedan usarla
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};